import { AssetClass } from '@/entities/assets'

import { TRANSACTION_TYPE } from '@/entities/transactions/utils/const'

import { WizardAccountMode, WizardCashFlow, WizardInvestor, WizardMark, WizardValues } from '../utils/types'
import { createHolding, ensureAccount, ensureTagIds, postTransaction } from '../utils/create'
import { useAssetPricesStore } from '@/store/assets/prices'

/** Same string the legacy Private fund wizard files commitments under. */
const FUND_TYPE = 'private fund'

/**
 * One bundle per entity — an asset and an `add position` transaction each,
 * held in that entity's own account, for the full committed amount — plus
 * whatever the cash-flows step logged against that same entity.
 *
 * The sibling of PrivateFundSubscribeAndCommit/create.ts, minus the split:
 * there `investor.share` is each bundle's own slice of one commitment, here
 * every bundle gets the whole `values.amount`, because each entity is an
 * independent holder rather than a share of one pool.
 *
 * The name is the fund's, the same for every bundle: it is one fund, however
 * many entities independently hold it. The ticker is what tells them apart —
 * `AssetClass.validate` requires one to be unique, and "Sequoia Growth III"
 * twice over would collide the moment a second entity joined.
 *
 * Sequential, not `Promise.all`: two entities can both resolve to the same
 * new account name — a family that invests through the same trust twice in
 * one sentence — and firing both creates at once would race to make it twice.
 */
export const create = async (values: WizardValues): Promise<AssetClass[]> => {
  const tagIds = await ensureTagIds(values.tags)
  const investors: WizardInvestor[] = values.investors ?? []
  const cashFlows: WizardCashFlow[] = values.cashFlows ?? []

  const funds: AssetClass[] = []

  for (const [index, investor] of investors.entries()) {
    const accountId = await ensureAccount({
      id: investor.accountMode === WizardAccountMode.NEW ? undefined : investor.accountId,
      name: investor.accountName,
      type: investor.accountType,
      currency: values.currency,
    })

    const fund = await createHolding({
      accountId,
      transactionType: TRANSACTION_TYPE.ADD_POSITION,
      name: values.assetName,
      ticker: `${values.assetName} (${investor.accountName})`,
      type: values.assetType || FUND_TYPE,
      currency: values.currency,
      description: values.description,
      amount: values.amount,
      date: values.date,
      tagIds,
    })

    /*
     * The NAVs the valuation step recorded, at full value for every bundle:
     * unlike the split flow's own pro-rating, each entity independently
     * holds the whole position, so a mark of $X applies to each of them at
     * $X, not at a fraction of it.
     */
    await createInvestorMarks(fund.id, values.prices)

    /*
     * This entity's own capital calls, fees and distributions — logged
     * against the bundle just created, in the same account, one call each so
     * a later one failing does not leave an earlier one half-posted.
     */
    await createInvestorCashFlows(
      accountId,
      fund.id,
      values.currency,
      cashFlows.filter(flow => flow.investorIndex === index),
    )

    funds.push(fund)
  }

  return funds
}

/** This entity's own rows from the cash-flows step, posted as follow-up
 * transactions against the bundle `create` just made for them. */
const createInvestorCashFlows = async (
  accountId: string,
  assetId: string,
  currency: string,
  flows: WizardCashFlow[],
): Promise<void> => {
  for (const flow of flows) {
    await postTransaction({
      accountId,
      assetId,
      transactionType: flow.type,
      currency,
      amount: flow.amount,
      date: flow.date,
    })
  }
}

/** One entity's own copy of the valuation step's marks — see `create`'s own
 * note on why this is never pro-rated the way the split flow's is. */
const createInvestorMarks = async (
  assetId: string,
  marks: WizardMark[] = [],
): Promise<void> => {
  const pricesStore = useAssetPricesStore()

  for (const mark of marks) {
    await pricesStore.store({
      asset_id: assetId,
      date: mark.date,
      close: mark.close,
      source: mark.source,
    })
  }
}
